import { motion, useReducedMotion } from "framer-motion";
import { ArrowUpRight, Smartphone } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";

const ease = [0.22, 1, 0.36, 1];

const COSMOS_URL = "https://mehedi-cosmos.vercel.app";

const stack = ["Next.js 15", "React 19", "Three.js", "R3F", "GSAP", "GLSL", "TypeScript"];

export default function CosmosLab() {
  const reduceMotion = useReducedMotion();
  const videoRef = useRef(null);
  const coverRef = useRef(null);
  const resetTimer = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [hoverCapable, setHoverCapable] = useState(false);

  // Detect hover-capable devices (desktop/laptop) vs touch (phone/tablet).
  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return undefined;
    const mq = window.matchMedia("(hover: hover) and (pointer: fine)");
    const update = () => setHoverCapable(mq.matches);
    update();
    mq.addEventListener?.("change", update);
    return () => mq.removeEventListener?.("change", update);
  }, []);

  const playVideo = useCallback(() => {
    const video = videoRef.current;
    if (!video || reduceMotion) return;
    if (resetTimer.current) {
      clearTimeout(resetTimer.current);
      resetTimer.current = null;
    }
    // Drive the crossfade immediately (state-first), then start playback.
    setIsPlaying(true);
    video.play().catch(() => {});
  }, [reduceMotion]);

  // Fade out, then rewind to frame 0 — the crossfade hides the rewind "snap".
  const stopVideo = useCallback(() => {
    const video = videoRef.current;
    if (!video) return;
    setIsPlaying(false);
    resetTimer.current = setTimeout(() => {
      video.pause();
      try {
        video.currentTime = 0;
      } catch {
        /* ignore */
      }
    }, 650);
  }, []);

  // Touch devices: play while the cover is >50% visible, reset otherwise.
  useEffect(() => {
    if (reduceMotion || hoverCapable) return undefined;
    const cover = coverRef.current;
    if (!cover) return undefined;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.intersectionRatio >= 0.5) {
          playVideo();
        } else {
          stopVideo();
        }
      },
      { threshold: [0, 0.5, 1] }
    );

    observer.observe(cover);
    return () => observer.disconnect();
  }, [reduceMotion, hoverCapable, playVideo, stopVideo]);

  useEffect(() => () => resetTimer.current && clearTimeout(resetTimer.current), []);

  return (
    <section id="lab" className="section-shell border-t-2 border-line py-20 text-ink md:py-28">
      <div className="mx-auto max-w-[100rem] px-4 md:px-8">
        <div className="grid gap-6 border-b-2 border-line pb-8 lg:grid-cols-[1fr_0.8fr] lg:items-end">
          <div>
            <p className="section-kicker">Lab / Experiment</p>
            <h2 className="section-heading">
              Something a<br />little different.
            </h2>
          </div>
          <p className="section-copy text-sm md:text-base">
            Mehedi Cosmos is an interactive 3D version of my portfolio. Instead of scrolling through
            sections, you fly through a solar system where each planet represents a part of my work.
          </p>
        </div>

        {/* main strip */}
        <div className="mt-8 grid border-2 border-line lg:grid-cols-2">
          {/* left: interactive video cover (hover to play on desktop, visibility on mobile) */}
          <motion.a
            ref={coverRef}
            href={COSMOS_URL}
            target="_blank"
            rel="noopener noreferrer"
            aria-label="Launch Mehedi Cosmos, an experimental 3D portfolio (opens in a new tab)"
            onMouseEnter={hoverCapable ? playVideo : undefined}
            onMouseLeave={hoverCapable ? stopVideo : undefined}
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.3 }}
            transition={{ duration: 0.55, ease }}
            className="group relative flex aspect-video items-end overflow-hidden border-b-2 border-line bg-ink lg:aspect-auto lg:min-h-[26rem] lg:border-b-0 lg:border-r-2"
          >
            {/* idle first-frame poster (always present, also the reduced-motion state) */}
            <img
              src="/cosmos-cover.jpg"
              alt="Mehedi Cosmos — an immersive 3D galaxy portfolio"
              loading="lazy"
              className={`absolute inset-0 h-full w-full object-cover object-center transition-transform duration-[900ms] ease-out ${
                isPlaying ? "scale-[1.04]" : "scale-100"
              }`}
            />

            {/* video overlay — crossfades over the poster while playing */}
            {!reduceMotion && (
              <video
                ref={videoRef}
                className={`absolute inset-0 h-full w-full object-cover object-center transition-[opacity,transform] duration-[900ms] ease-out ${
                  isPlaying ? "scale-[1.04] opacity-100" : "scale-100 opacity-0"
                }`}
                src="/cosmos-cover.mp4"
                poster="/cosmos-cover.jpg"
                muted
                loop
                playsInline
                preload="metadata"
                aria-hidden="true"
              />
            )}

            {/* legibility gradient for the label */}
            <div
              aria-hidden="true"
              className="pointer-events-none absolute inset-0 bg-gradient-to-t from-ink/80 via-ink/10 to-transparent"
            />

            <span className="relative m-4 font-mono text-[10px] uppercase tracking-[0.2em] text-paper/75">
              mehedi-cosmos.vercel.app ↗
            </span>
          </motion.a>

          {/* right: copy + stack + CTA */}
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.3 }}
            transition={{ duration: 0.55, delay: 0.08, ease }}
            className="flex flex-col justify-between bg-paper p-7 md:p-9"
          >
            <div>
              <span className="inline-flex items-center gap-2 whitespace-nowrap border-2 border-line bg-paper2 px-3 py-1 font-mono text-[9px] uppercase tracking-[0.14em] text-inksoft md:text-[10px] md:tracking-[0.2em]">
                <span className="h-2 w-2 bg-accent" />
                Mehedi Cosmos — Experimental
              </span>
              <h3 className="mt-4 font-display text-2xl uppercase leading-[1.05] md:text-4xl md:leading-[0.95]">
                Fly through my work,<br />planet by planet.
              </h3>
              <p className="mt-3 text-sm leading-relaxed text-ink md:mt-4 md:text-lg">
                It's completely optional — but if you're curious, I'd love for you to check it out.
              </p>

              {/* built with — 2-col grid on mobile, free-wrap on larger screens */}
              <div className="mt-6">
                <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-inksoft/60">Built with</p>
                <ul className="mt-2 grid grid-cols-2 gap-2 sm:flex sm:flex-wrap">
                  {stack.map((tech) => (
                    <li
                      key={tech}
                      className="border border-line px-2.5 py-1 text-center font-mono text-[11px] uppercase tracking-[0.12em] text-inksoft sm:text-left"
                    >
                      {tech}
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            <div className="mt-6 flex flex-col items-start gap-3 sm:mt-7 sm:flex-row sm:flex-wrap sm:items-center sm:gap-4">
              <a
                href={COSMOS_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="cta-primary w-full justify-center sm:w-auto"
              >
                <span className="sm:hidden">Launch Cosmos</span>
                <span className="hidden sm:inline">Launch Mehedi Cosmos</span>
                <ArrowUpRight size={14} />
              </a>
              <span className="font-mono text-[11px] uppercase tracking-[0.14em] text-inksoft">
                Opens in a new tab ↗
              </span>
            </div>
          </motion.div>
        </div>

        {/* mobile P.S. — brutalist callout */}
        <div className="mt-6 flex items-center gap-4 border-2 border-line bg-ink px-5 py-4 text-paper shadow-[6px_6px_0_0_var(--accent)] md:px-7 md:py-5">
          <Smartphone size={26} className="shrink-0 text-accent" />
          <p className="text-sm leading-relaxed md:text-base">
            <span className="font-mono text-xs font-bold uppercase tracking-[0.2em] text-accent">P.S.&nbsp;&nbsp;</span>
            On a phone? This one's built for bigger screens — come back on a laptop when you can,
            it's worth it. 🙂
          </p>
        </div>
      </div>
    </section>
  );
}
